import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, Transition as _Transition, createElementVNode as _createElementVNode, Fragment as _Fragment } from "vue";
const _hoisted_1 = { key: 0 };
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useGoals, pendingGoalsRevalidate } from '@/composables/useGoals';
import { useApi } from '@/composables/useApi';
import { usePolling } from '@/composables/usePolling';
import SpendingSummaryCard from './elements/SpendingSummaryCard.vue';
import { getSpendingAnalysis, getTransactionClassificationTree, buildSpendingOverviewBreakdown, getDefaultMonthRange } from '@/composables/useSpendingAnalysis';
import { formatResponseRange } from '@/utils/helpers';
import { snapToSpendingCycle } from './elements/spendingAnalysisFilterState';
import HeaderBlock from './elements/HeaderBlock.vue';
import PlanInfoPanel from './elements/PlanInfoPanel.vue';
import InfoPanel from './elements/InfoPanel.vue';
import UncategorisedCard from './elements/UncategorisedCard.vue';
import GoalInfoPanel from './elements/GoalInfoPanel.vue';
import AdButton from './elements/AdButton.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ActivityPage',
    setup(__props) {
        const router = useRouter();
        const state = useAppState();
        useAuth();
        const { getCustomer, getCustomerTags } = useCustomer();
        const { getBankingInfo, getRecurringPayments } = useBanking();
        const { getSavingGoals } = useGoals();
        const api = useApi();
        const { poll, getPollRemaining } = usePolling('banking');
        const selectedTotals = ref(null);
        const goalsReady = ref(false);
        const totalSavingGoals = ref(0);
        const totalSavingGoalsSaved = ref(0);
        const totalGoals = ref(0);
        const recurringPayments = ref(null);
        const customerTags = ref(null);
        const pollingComplete = ref(!localStorage.getItem('poll_remaining_banking'));
        const v2SummaryRaw = ref(null);
        const v2ClassificationTabs = ref([]);
        const v2UntaggedCount = computed(() => {
            const parents = Array.isArray(v2SummaryRaw.value?.parentCategories) ? v2SummaryRaw.value.parentCategories : [];
            const seen = new Set();
            let count = 0;
            for (const p of parents) {
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    const sub = t.subClassification;
                    if (sub && sub !== 'Uncategorised' && sub !== 'Uncategorized')
                        continue;
                    const id = t.id ?? `${t.merchantName}-${t.timestamp}-${t.amount}`;
                    if (seen.has(id))
                        continue;
                    seen.add(id);
                    count++;
                }
            }
            return count;
        });
        const plannedCategories = computed(() => {
            const cats = v2SummaryRaw.value?.categories ?? [];
            return cats.filter((c) => c.plan);
        });
        const totalPlan = computed(() => {
            return Math.round(plannedCategories.value.reduce((a, c) => a + (c.plan?.amount ?? 0), 0));
        });
        const totalSpend = computed(() => {
            return Math.round(plannedCategories.value.reduce((a, c) => a + Math.abs(c.totalOut ?? 0), 0));
        });
        const hasPlans = computed(() => plannedCategories.value.length > 0);
        const spendingPeriodLabel = computed(() => {
            const r = v2SummaryRaw.value;
            if (r?.fromDate && r?.toDate)
                return formatResponseRange(r.fromDate, r.toDate);
            return 'Spending Analysis';
        });
        const homeSpendingSummary = computed(() => {
            const r = v2SummaryRaw.value;
            if (!r)
                return null;
            const totalIn = r.totalIn ?? 0;
            const totalOut = r.totalOut ?? 0;
            const breakdown = buildSpendingOverviewBreakdown(r.parentCategories, (name) => v2ClassificationTabs.value.find(t => t.label === name)?.color ?? null);
            return {
                rangeLabel: '',
                net: r.net ?? (totalIn - totalOut),
                totalIn,
                totalOut,
                breakdown,
            };
        });
        const dummySpendingSummary = {
            rangeLabel: '',
            net: 1240,
            totalIn: 2800,
            totalOut: 1560,
            breakdown: [],
        };
        function fetchActivityV2Summary() {
            getTransactionClassificationTree((tree) => {
                if (tree?.parents) {
                    v2ClassificationTabs.value = tree.parents.map((p) => ({
                        label: p.name,
                        color: p.color ?? p.parent?.color ?? null,
                    }));
                }
            });
            const { fromDate, toDate } = getDefaultMonthRange(snapToSpendingCycle.value);
            getSpendingAnalysis(fromDate, toDate, { snapToSpendingCycle: snapToSpendingCycle.value }, (result) => {
                if (result)
                    v2SummaryRaw.value = result;
            });
        }
        const transactionAccounts = computed(() => {
            var ret = [];
            if (state.bankInfo == null) {
                return ret;
            }
            state.bankInfo.accounts.forEach((a) => {
                if (a.accountType.prettyName.toLowerCase() == "current") {
                    ret.push(a);
                }
            });
            return ret;
        });
        async function onLoad() {
            // getAppConfig inlined — fetch from S3 with no auth header
            const configUri = state.configUri;
            if (configUri) {
                try {
                    const origHeaders = api._httpInstance?.defaults?.headers?.common;
                    // Use fetch to avoid touching axios headers
                }
                catch (e) { /* ignore */ }
            }
            getCustomer(() => {
                getBankingInfo(() => {
                    fetchActivityV2Summary();
                    const pollRemaining = getPollRemaining();
                    if (pollRemaining) {
                        pollingComplete.value = false;
                        const initialCount = state.bankInfo.untaggedTransactionsCurrentCycleCount;
                        poll({
                            remaining: pollRemaining ?? 5,
                            interval: 3000,
                            fetch: (onSuccess) => getBankingInfo(onSuccess, true),
                            onPoll: () => { },
                            shouldStop: () => state.bankInfo.untaggedTransactionsCurrentCycleCount !== initialCount || state.bankInfo.untaggedTransactionsCurrentCycleCount === 0,
                            onFinish: () => {
                                pollingComplete.value = true;
                            },
                        });
                    }
                    if (state.bankInfo.spendingCycle.id == "00000000-0000-0000-0000-000000000000") {
                        router.push('/setupspendinganalysis');
                        return;
                    }
                });
                getRecurringPayments((res) => {
                    recurringPayments.value = res;
                });
                getCustomerTags((tags) => { customerTags.value = tags; });
                // If a recent goal create/delete hasn't propagated to the server's
                // /Cached payload yet, SWR+poll until it does so the summary count
                // updates instead of showing the pre-write totals.
                const revalidateGoals = pendingGoalsRevalidate.value;
                if (revalidateGoals)
                    pendingGoalsRevalidate.value = false;
                getSavingGoals(() => {
                    goalsReady.value = true;
                    createGoalsSummary();
                }, /* SWR */ revalidateGoals, /* poll */ revalidateGoals);
            });
        }
        function createGoalsSummary() {
            let totalTargetAmount = 0;
            if (state.savingGoals && state.savingGoals.length > 0) {
                state.savingGoals.forEach((goal) => {
                    totalTargetAmount += goal.targetAmount;
                });
            }
            totalSavingGoals.value = totalTargetAmount;
            let totalCurrentBalance = 0;
            if (state.savingGoals && state.savingGoals.length > 0) {
                state.savingGoals.forEach((goal) => {
                    totalCurrentBalance += goal.currentBalance;
                });
            }
            totalSavingGoalsSaved.value = totalCurrentBalance;
            totalGoals.value = state.savingGoals ? state.savingGoals.length : 0;
        }
        function navigateToSpendingAnalysis() {
            router.push('/SpendingAnalysis');
        }
        function navigateToPlan() {
            router.push('/SpendingPlan');
        }
        function navigateToGoals() {
            router.push('/Goals');
        }
        onMounted(() => {
            onLoad();
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_row = _resolveComponent("v-row");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, { title: "Activity" }),
                _createVNode(AiChatBanner),
                (!_unref(state).bankInfo)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 0,
                        type: "image",
                        class: "activity-skeleton-item"
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length == 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                _createVNode(_component_v_row, { class: "financialFitnessRow" }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                            default: _withCtx(() => [
                                                _createVNode(AdButton, {
                                                    class: "linkAccounts",
                                                    title: "Link Your Bank Accounts",
                                                    href: "/#/money?add=true",
                                                    subtitle: "Money Guided is designed to work with insights into your personal finances. Link your bank accounts to benefit from the power shown in the examples below!",
                                                    topicon: require('@/assets/coins.png'),
                                                    bottomrighticon: require('@/assets/go.png')
                                                }, null, 8, ["topicon", "bottomrighticon"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }),
                                _createVNode(_component_v_row, {
                                    class: "mt-2",
                                    style: { "opacity": "0.7", "filter": "blur(1px)" }
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                            default: _withCtx(() => [
                                                _createVNode(SpendingSummaryCard, {
                                                    compact: true,
                                                    compactTitle: "Spending Analysis",
                                                    data: dummySpendingSummary
                                                })
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }),
                                _createVNode(_component_v_row, {
                                    class: "mt-2",
                                    style: { "opacity": "0.7", "filter": "blur(1px)" }
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                            default: _withCtx(() => [
                                                _createVNode(InfoPanel, {
                                                    icon: require('@/assets/coins2.png'),
                                                    title: "Tag Transactions",
                                                    subtitle: "You have transactions to tag in your current pay cycle",
                                                    valueTitle: "Count",
                                                    value: 7
                                                }, null, 8, ["icon"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }),
                                _createVNode(_component_v_row, {
                                    class: "mt-2",
                                    style: { "opacity": "0.7", "filter": "blur(1px)" }
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                            default: _withCtx(() => [
                                                _createVNode(InfoPanel, {
                                                    icon: require('@/assets/coins.png'),
                                                    title: "13 Recurring payments",
                                                    subtitle: "Stay on top of your payments",
                                                    showArrow: ""
                                                }, null, 8, ["icon"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }),
                                _createVNode(_component_v_row, { style: { "opacity": "0.7", "filter": "blur(1px)" } }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                            default: _withCtx(() => [
                                                _createVNode(PlanInfoPanel, {
                                                    plan: 400,
                                                    spent: 234,
                                                    title: "Groceries Budget",
                                                    formatter: _unref(state).formatter
                                                }, null, 8, ["formatter"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }),
                                _createVNode(_component_v_row, { style: { "opacity": "0.7", "filter": "blur(1px)" } }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                            default: _withCtx(() => [
                                                _createVNode(GoalInfoPanel, {
                                                    goal: 300,
                                                    saved: 80,
                                                    title: "Sarah's New Bike",
                                                    formatter: _unref(state).formatter
                                                }, null, 8, ["formatter"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                })
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (!_unref(state).bankInfo)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 1,
                        type: "list-item-avatar-two-line",
                        class: "activity-skeleton-item"
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (pollingComplete.value && _unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && v2UntaggedCount.value > 0)
                            ? (_openBlock(), _createBlock(UncategorisedCard, {
                                key: 0,
                                class: "uncategorisedCard mt-2",
                                count: v2UntaggedCount.value,
                                onClick: _cache[0] || (_cache[0] = ($event) => (_unref(router).push('/TransactionBulk')))
                            }, null, 8, ["count"]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && !v2SummaryRaw.value)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 2,
                        type: "list-item-avatar-two-line",
                        class: "activity-skeleton-item"
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && homeSpendingSummary.value)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "mt-2"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", {
                                                class: "homeSpendingSummaryWrap",
                                                onClick: _cache[1] || (_cache[1] = ($event) => (navigateToSpendingAnalysis()))
                                            }, [
                                                _createVNode(SpendingSummaryCard, {
                                                    compact: true,
                                                    compactTitle: spendingPeriodLabel.value,
                                                    data: homeSpendingSummary.value
                                                }, null, 8, ["compactTitle", "data"])
                                            ])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && homeSpendingSummary.value)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "mt-2"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                            _createVNode(InfoPanel, {
                                                href: "/#/SpendingTreemap",
                                                icon: require('@/assets/spendingtreemap2.png'),
                                                title: "Where your money goes",
                                                showArrow: ""
                                            }, null, 8, ["icon"])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && !v2SummaryRaw.value)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 3,
                        type: "list-item-avatar-two-line",
                        class: "activity-skeleton-item"
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && v2SummaryRaw.value && hasPlans.value)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "mt-2"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                            _createVNode(PlanInfoPanel, {
                                                onClick: _cache[2] || (_cache[2] = ($event) => (navigateToPlan())),
                                                plan: totalPlan.value,
                                                spent: totalSpend.value,
                                                title: "Spending plan",
                                                formatter: _unref(state).formatter
                                            }, null, 8, ["plan", "spent", "formatter"])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (!recurringPayments.value)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 4,
                        type: "list-item-avatar-two-line",
                        class: "activity-skeleton-item"
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (recurringPayments.value && recurringPayments.value.payments.length > 0)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "mt-2"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                            _createVNode(InfoPanel, {
                                                href: '/#/RecurringPayments',
                                                icon: require('@/assets/coins.png'),
                                                title: `${recurringPayments.value.payments.length} Recurring payments`,
                                                subtitle: "Stay on top of your payments",
                                                showArrow: ""
                                            }, null, 8, ["icon", "title"])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (!customerTags.value)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 5,
                        type: "list-item-avatar-two-line",
                        class: "activity-skeleton-item"
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (customerTags.value)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "mt-2"
                            }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                            _createVNode(InfoPanel, {
                                                href: "/#/CustomerTagSpend",
                                                valueHref: "/#/CustomerTagSpend",
                                                icon: require('@/assets/Frame.png'),
                                                title: "Custom #Tags",
                                                subtitle: "Your spending, categorised your way",
                                                valueTitle: "Count",
                                                value: customerTags.value.length
                                            }, null, 8, ["icon", "value"])
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && !goalsReady.value)
                    ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                        key: 6,
                        type: "list-item-avatar-two-line",
                        class: "activity-skeleton-item"
                    }))
                    : _createCommentVNode("", true),
                _createVNode(_Transition, null, {
                    default: _withCtx(() => [
                        (_unref(state).bankInfo && _unref(state).bankInfo.accounts.length > 0 && goalsReady.value)
                            ? (_openBlock(), _createBlock(_component_v_row, {
                                key: 0,
                                class: "mt-2"
                            }, {
                                default: _withCtx(() => [
                                    (totalGoals.value > 0)
                                        ? (_openBlock(), _createBlock(_component_v_col, { key: 0 }, {
                                            default: _withCtx(() => [
                                                _createVNode(GoalInfoPanel, {
                                                    href: "/#/Goals",
                                                    goal: totalSavingGoals.value,
                                                    saved: totalSavingGoalsSaved.value,
                                                    title: totalGoals.value + ' Savings Goal' + (totalGoals.value > 1 ? 's' : ''),
                                                    formatter: _unref(state).formatter
                                                }, null, 8, ["goal", "saved", "title", "formatter"])
                                            ]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true),
                                    (totalGoals.value == 0)
                                        ? (_openBlock(), _createBlock(_component_v_col, { key: 1 }, {
                                            default: _withCtx(() => [
                                                _createVNode(InfoPanel, {
                                                    href: '/#/Goals',
                                                    valueHref: '/#/Goals',
                                                    icon: require('@/assets/coins2.png'),
                                                    title: "Saving Goals",
                                                    subtitle: "Save for the important things in life"
                                                }, null, 8, ["icon"])
                                            ]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true)
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }),
                _cache[3] || (_cache[3] = _createElementVNode("div", { style: { "clear": "both", "height": "60px" } }, null, -1))
            ], 64));
        };
    }
});
